import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Activity, Database, Server, Send, ShieldBan, Inbox,
  Cpu, RefreshCw, Boxes, Gauge, Radio, LayoutDashboard, Settings as SettingsIcon, FileCode2, Megaphone,
} from "lucide-react";
import Settings from "./Settings";
import Templates from "./Templates";
import Campaigns from "./campaigns/Campaigns";
import Login from "./Login";
import Register from "./Register";
import { AuthProvider, useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const SCENARIOS = [
  { label: "Normal send", sub: "→ SENT", to: "alice@gmail.com" },
  { label: "Hard bounce", sub: "5.1.1 → suppress", to: "bounce@example.com" },
  { label: "Mailbox full", sub: "4.2.2 → retry", to: "full@example.com" },
  { label: "Provider fail", sub: "auth → failover", to: "fail@example.com" },
];

const badgeClass = (s) => `badge b-${(s || "").toLowerCase()}`;

function Metric({ k, v, accent, fill }) {
  return (
    <div className={`metric accent-${accent}`} data-testid={`metric-${k.toLowerCase()}`}>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      <div className="bar"><i style={{ width: `${fill}%` }} /></div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

function AppInner() {
  const { user } = useAuth();
  const isRegister = window.location.pathname === "/register" || window.location.search.includes("token=");

  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [emails, setEmails] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [dlq, setDlq] = useState([]);
  const [to, setTo] = useState("alice@gmail.com");
  const [subject, setSubject] = useState("Welcome aboard");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [tick, setTick] = useState(0);
  const [view, setView] = useState("dashboard");

  const refresh = useCallback(async () => {
    try {
      const [h, s, p, e, sup, d] = await Promise.all([
        axios.get(`${API}/health`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/providers`),
        axios.get(`${API}/emails?limit=15`),
        axios.get(`${API}/suppressions?limit=10`),
        axios.get(`${API}/dlq`),
      ]);
      setHealth(h.data); setStats(s.data); setProviders(p.data);
      setEmails(e.data); setSuppressions(sup.data); setDlq(d.data);
    } catch (err) {
      setHealth({ status: "unreachable", checks: {} });
    }
  }, [user]);

  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
    refresh();
    const id = setInterval(() => { refresh(); setTick((t) => t + 1); }, 3000);
    return () => clearInterval(id);
  }, [refresh]);

  const send = useCallback(async (addr) => {
    setSending(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/emails`, {
        to: addr || to,
        subject: subject || "Test message",
      });
      setResult(
        `${data.created ? "queued" : "idempotent hit"} · ${data.status} · log ${data.emailLogId.slice(0, 8)}`,
      );
      refresh();
    } catch (err) {
      setResult(`error: ${err?.response?.data?.message || err.message}`);
    } finally {
      setSending(false);
    }
  }, [to, subject, refresh]);

  const byStatus = stats?.emailsByStatus || {};
  const total = stats?.totalEmails || 0;
  const pct = (n) => (total ? Math.max(6, Math.round((n / total) * 100)) : 0);
  const q = stats?.queue || {};
  const ok = health?.status === "ok";

  const uptime = useMemo(() => {
    const s = health?.uptimeSeconds || 0;
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  }, [health]);

  if (isRegister) return <Register />;
  if (user === undefined) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
      <div style={{ color: "var(--text-muted)", fontSize: 14 }}>Loading…</div>
    </div>
  );

  return (
    <div className="console">
      <div className="wrap">
        {/* top bar */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark"><Send size={20} strokeWidth={2.4} /></div>
            <div>
              <h1>Mail Engine</h1>
              <p>Multi-provider delivery infrastructure · headless API</p>
            </div>
          </div>
          <div className="status-cluster">
            <div className="nav-toggle" data-testid="view-nav">
              <button className={`nav-btn ${view === "dashboard" ? "active" : ""}`} data-testid="nav-dashboard" onClick={() => setView("dashboard")}>
                <LayoutDashboard size={14} /> Dashboard
              </button>
              <button className={`nav-btn ${view === "settings" ? "active" : ""}`} data-testid="nav-settings" onClick={() => setView("settings")}>
                <SettingsIcon size={14} /> Settings
              </button>
              <button className={`nav-btn ${view === "templates" ? "active" : ""}`} data-testid="nav-templates" onClick={() => setView("templates")}>
                <FileCode2 size={14} /> Templates
              </button>
              <button className={`nav-btn ${view === "campaigns" ? "active" : ""}`} data-testid="nav-campaigns" onClick={() => setView("campaigns")}>
                <Megaphone size={14} /> Campaigns
              </button>
            </div>
            <div className="svc-dots">
              <span className={`svc ${health?.checks?.mongodb === "up" || health?.checks?.postgres === "up" ? "up" : "down"}`}>
                <span className="dot" /><Database size={12} /> mongo
              </span>
              <span className={`svc up`}>
                <span className="dot" /><Server size={12} /> asyncio
              </span>
            </div>
            <span className={`pill ${ok ? "ok" : "bad"}`} data-testid="health-status">
              <span className="dot pulse" />
              {ok ? "Operational" : (health?.status || "…")}
            </span>
            <span className="pill" title="uptime"><Activity size={13} /> {uptime}</span>
          </div>
        </div>

        {view !== "dashboard" ? (
          <div className="sec-head" style={{ marginTop: 30 }}><h2>{view === "settings" ? "Settings" : view === "templates" ? "Templates" : "Campaigns"}</h2><div className="rule" /><span className="count">{view === "settings" ? "deliverability · security · providers" : view === "templates" ? "MJML editor · live preview · merge tags" : "audience · content · schedule"}</span></div>
        ) : null}
        {view === "settings" ? <Settings /> : view === "templates" ? <Templates /> : view === "campaigns" ? <Campaigns /> : (
        <>
        {/* metrics */}
        <div className="sec-head">
          <h2>Throughput</h2><div className="rule" />
          <span className="count">{total} total · auto-refresh 3s</span>
        </div>
        <div className="metrics">
          <Metric k="SENT" v={byStatus.SENT || 0} accent="blue" fill={pct(byStatus.SENT || 0)} />
          <Metric k="DELIVERED" v={byStatus.DELIVERED || 0} accent="green" fill={pct(byStatus.DELIVERED || 0)} />
          <Metric k="DEFERRED" v={byStatus.DEFERRED || 0} accent="amber" fill={pct(byStatus.DEFERRED || 0)} />
          <Metric k="BOUNCED" v={byStatus.BOUNCED || 0} accent="red" fill={pct(byStatus.BOUNCED || 0)} />
          <Metric k="SUPPRESSED" v={stats?.suppressions || 0} accent="violet" fill={pct(byStatus.SUPPRESSED || 0)} />
          <Metric k="DEAD-LETTER" v={stats?.deadLetter || 0} accent="red" fill={pct(stats?.deadLetter || 0)} />
        </div>

        {/* queue strip */}
        <div className="sec-head">
          <h2>Queue</h2><div className="rule" />
          <span className="count">asyncio · email-delivery</span>
        </div>
        <div className="metrics">
          <Metric k="WAITING" v={q.waiting || 0} accent="blue" fill={20} />
          <Metric k="ACTIVE" v={q.active || 0} accent="green" fill={20} />
          <Metric k="DELAYED" v={q.delayed || 0} accent="amber" fill={20} />
          <Metric k="COMPLETED" v={q.completed || 0} accent="green" fill={20} />
          <Metric k="FAILED" v={q.failed || 0} accent="red" fill={20} />
          <Metric k="PROVIDERS" v={stats?.providers || 0} accent="violet" fill={20} />
        </div>

        {/* main cols */}
        <div className="cols" style={{ marginTop: 34 }}>
          {/* dispatch */}
          <div className="panel" data-testid="dispatch-panel">
            <div className="p-head"><Radio size={15} /><h3>Dispatch</h3><span className="hint">live pipeline</span></div>
            <div className="p-body">
              <div className="field">
                <label>Recipient</label>
                <input data-testid="test-email-input" value={to} onChange={(e) => setTo(e.target.value)} placeholder="user@domain.com" />
              </div>
              <div className="field">
                <label>Subject</label>
                <input data-testid="test-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
              </div>
              <button className="btn" data-testid="send-email-btn" disabled={sending} onClick={() => send()}>
                <Send size={15} /> {sending ? "Dispatching…" : "Dispatch message"}
              </button>

              <div className="scenarios">
                {SCENARIOS.map((s) => (
                  <button
                    key={s.to}
                    className="chip"
                    data-testid={`scenario-${s.to.split("@")[0]}`}
                    onClick={() => { setTo(s.to); send(s.to); }}
                  >
                    <b>{s.label}</b>{s.sub}
                  </button>
                ))}
              </div>

              {result && <div className="dispatch-result" data-testid="dispatch-result">{result}</div>}
            </div>
          </div>

          {/* recent emails */}
          <div className="panel" data-testid="emails-panel">
            <div className="p-head"><Inbox size={15} /><h3>Recent messages</h3><span className="hint">source of truth · EmailLog</span></div>
            <div className="scroll">
              <table className="tbl">
                <thead>
                  <tr><th>Recipient</th><th>Status</th><th>MTA</th><th>Retries</th><th>Provider ID</th></tr>
                </thead>
                <tbody data-testid="emails-tbody">
                  {emails.length === 0 && <tr><td colSpan={5} className="empty">No messages yet — dispatch one.</td></tr>}
                  {emails.map((e) => (
                    <tr key={e.id} data-testid={`email-row-${e.id.slice(0, 8)}`}>
                      <td className="mono-strong">{e.toAddress}</td>
                      <td><span className={badgeClass(e.status)}>{e.status}</span></td>
                      <td className={e.mtaStatusCode ? "code-tag" : ""}>{e.mtaStatusCode || "—"}</td>
                      <td>{e.retryCount}</td>
                      <td>{e.providerMessageId ? e.providerMessageId.slice(0, 16) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* providers */}
        <div className="sec-head"><h2>Providers</h2><div className="rule" /><span className="count">routing waterfall</span></div>
        <div className="panel">
          <table className="tbl">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Priority</th><th>Health</th><th>Warmup</th><th>Daily usage</th><th>Failures</th></tr>
            </thead>
            <tbody data-testid="providers-tbody">
              {providers.length === 0 && <tr><td colSpan={7} className="empty">No providers configured.</td></tr>}
              {providers.map((p) => (
                <tr key={p.id}>
                  <td className="mono-strong">{p.name}</td>
                  <td>{p.type}</td>
                  <td>{p.priority}</td>
                  <td><span className={`badge ${p.healthStatus === "HEALTHY" ? "b-delivered" : "b-bounced"}`}>{p.healthStatus}</span></td>
                  <td>{p.warmupStatus}</td>
                  <td>{p.dailySentCount} / {p.dailyLimit}</td>
                  <td>{p.consecutiveFailures}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* suppression + dlq */}
        <div className="cols" style={{ marginTop: 34 }}>
          <div className="panel" data-testid="suppression-panel">
            <div className="p-head"><ShieldBan size={15} /><h3>Suppression</h3><span className="hint">global blocklist</span></div>
            <div className="scroll">
              <table className="tbl">
                <thead><tr><th>Email</th><th>Reason</th><th>Code</th></tr></thead>
                <tbody data-testid="suppression-tbody">
                  {suppressions.length === 0 && <tr><td colSpan={3} className="empty">Empty.</td></tr>}
                  {suppressions.map((s) => (
                    <tr key={s.id}>
                      <td className="mono-strong">{s.email}</td>
                      <td><span className="badge b-suppressed">{s.reason}</span></td>
                      <td className={s.mtaStatusCode ? "code-tag" : ""}>{s.mtaStatusCode || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel" data-testid="dlq-panel">
            <div className="p-head"><Boxes size={15} /><h3>Dead-letter queue</h3><span className="hint">terminal failures</span></div>
            <div className="scroll">
              <table className="tbl">
                <thead><tr><th>Recipient</th><th>Kind</th><th>Code</th><th>Attempts</th></tr></thead>
                <tbody data-testid="dlq-tbody">
                  {dlq.length === 0 && <tr><td colSpan={4} className="empty">Empty.</td></tr>}
                  {dlq.map((d) => (
                    <tr key={d.id}>
                      <td className="mono-strong">{d.to || "—"}</td>
                      <td><span className="badge b-failed">{d.failureKind}</span></td>
                      <td className={d.mtaStatusCode ? "code-tag" : ""}>{d.mtaStatusCode || "—"}</td>
                      <td>{d.totalAttempts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </>
        )}

        <div className="foot">
          <Cpu size={12} style={{ verticalAlign: "-2px" }} /> FastAPI · MongoDB · asyncio —
          headless engine, this console is a thin ops layer over the API ·{" "}
          <Gauge size={12} style={{ verticalAlign: "-2px" }} /> refresh #{tick}
          {"  "}·{"  "}
          <a href={`${API}`} target="_blank" rel="noreferrer">GET /api</a>
        </div>
      </div>
    </div>
  );
}
